import { Component, Input, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Configuration } from "@app/app.constants";
import { IncidentStatus } from "@app/incident/models/incident-status.enum";
import { IncidentPeriodicNotificationService } from "@app/incident/services/incident-periodicnotification.service";
import { PeriodicNotificationIncidentModel } from "@app/mailmessages/models/periodicNotificationIncidentModel";
import { PeriodicNotificationMoiService } from "@app/mailmessages/service/periodic-notification-moi.service";
import { CerrixEditorComponent } from "@app/shared/components/cerrix-editor/cerrix-editor.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { getFormControl } from "@methods/CommonMethods";
import { isEmpty } from "@methods/isEmpty";
import { isGuid } from "@methods/uniqueMethods";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabService } from "@services/tabs/TabService";
import { TranslocoService } from "@jsverse/transloco";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "uc-periodic-notification-detail-incidents",
    templateUrl: "./periodic-notification-detail-incidents.component.html",
    styleUrls: ["./periodic-notification-detail-incidents.component.scss"],
    standalone: false
})
export class PeriodicNotificationDetailIncidentsComponent implements OnInit {
    private _pnService = inject(PeriodicNotificationMoiService);
    private _incidentNotificationService = inject(IncidentPeriodicNotificationService);
    private toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private transloco = inject(TranslocoService);

    @ViewChild("incidenteditor") editorComponent: CerrixEditorComponent;
    @Input() module: ModuleType;
    @Input() id: string;
    @Input() cerrixTab: TabModel;

    pageReady = false;

    notification: any;
    keywords: string[];
    orgs: CerrixTreeItem[];
    mailEnabled: boolean;

    dayNumbers: IdNameCombination[] = [];
    periods: any;
    incidentStatuses: any[] = [];
    userRoles: any[] = [];

    incidentForm: FormGroup;
    isEmpty = false;
    organizations: any;
    organizationRequest: any;
    noOrganizationsSelectedDescription = Configuration.NoOrganizationsSelectedDescription;

    subjectHasFocus: boolean;

    async ngOnInit() {
        if (isGuid(this.id)) {
            var notification = await firstValueFrom(
                this._incidentNotificationService.getNotification(String(this.id))
            );
            this.cerrixTab.name = notification.name;
        } else {
            var notification = new PeriodicNotificationIncidentModel();
            notification.name = "";
            notification.subject = "";
            notification.message = "";
            notification.period = null;
            notification.active = true;
            notification.moduleType = ModuleType.Incidents;
            notification.organizations = [];
            notification.incidentStatuses = [];
            notification.userRoleIDs = [];
        }
        this.keywords = await firstValueFrom(
            this._incidentNotificationService.getNotificationKeywords()
        );
        this.organizationRequest = this._pnService.getOrganizations();

        this.notification = notification;
        this.mailEnabled = this.notification.active;

        this.incidentStatuses = this.getIncidentStatusOptions();

        var standingdata = await firstValueFrom(
            this._incidentNotificationService.getStandingDataForNotification()
        );

        this.userRoles = standingdata.roles;

        this.periods = standingdata.periods;

        if (this.notification.period) {
            this.setDayNumbers(this.periods.find((x) => x.id === this.notification.period));
        }

        this.initFormGroup(this.notification);
        this.pageReady = true;
    }

    private getIncidentStatusOptions(): { ID: number; Name: string }[] {
        return Object.keys(IncidentStatus)
            .filter((key) => !isNaN(Number(IncidentStatus[key])))
            .map((key) => ({
                ID: IncidentStatus[key],
                Name: key,
            }))
            .filter(
                (item) => item.ID !== IncidentStatus.Closed && item.ID !== IncidentStatus.Rejected
            );
    }

    setDayNumbers(period: any): void {
        this.dayNumbers = [];

        if (!period) {
            return;
        }

        for (let i = 1; i <= period.maxDayNumber; i++) {
            this.dayNumbers.push({ ID: i, Name: i.toString() });
        }
    }

    initFormGroup(notification: PeriodicNotificationIncidentModel) {
        this.incidentForm = new FormGroup({
            name: new FormControl(notification.name, Validators.required),
            active: new FormControl(notification.active),
            subject: new FormControl(notification.subject, Validators.required),
            organizations: new FormControl(notification.organizations),
        });

        this.incidentForm.addControl(
            "period",
            new FormControl(notification.period, Validators.required)
        );
        this.incidentForm.addControl(
            "incidentStatuses",
            new FormControl(notification.incidentStatuses)
        );
        this.incidentForm.addControl("userRoleIDs", new FormControl(notification.userRoleIDs));
        this.incidentForm.addControl(
            "dayNumber",
            new FormControl(notification.dayNumber, Validators.required)
        );

        this.incidentForm.valueChanges.subscribe(() =>
            FormValidationHelper.mapToModel(this.incidentForm, this.notification)
        );
    }

    onChangeMessage(message: string) {
        this.notification.message = message;
    }

    addKeyword(keyword: string) {
        if (this.subjectHasFocus) {
            const ctrl = getFormControl(this.incidentForm, "subject");
            const value = ctrl.value;
            ctrl.setValue(value + " [" + keyword + "]");
        } else {
            this.editorComponent.insertText(" [" + keyword + "]");
        }
    }

    async save() {
        this.isEmpty = false;
        if (!this.notification.organizations) {
            this.notification.organizations = [];
        }
        if (!this.validate()) {
            return;
        }

        const savingPrompt = this._promptService.loader(
            this.transloco.translate("mailmessages.periodicDetail.savingChanges")
        );
        var savePromise = firstValueFrom(
            this._incidentNotificationService
                .saveNotification(this.notification)
                .pipe(finalize(() => savingPrompt.close()))
        );
        await savePromise
            .then((storedId: string) => {
                this.id = storedId;
                this._tabService.refresh(this._pages.MailMessages);
                this.cerrixTab.id = storedId;
                this.cerrixTab.refresh();
                this.toastr.success("", this.transloco.translate("mailmessages.periodicDetail.updateCompleted"));
            })
            .catch((_) => {
                this.toastr.error("", this.transloco.translate("mailmessages.periodicDetail.updateFailed"));
            });
    }

    deleteClick() {
        this._incidentNotificationService
            .deleteNotification(this.notification.id)
            .subscribe((_) => {
                this._tabService.refresh(this._pages.MailMessages);
                this.toastr.success(this.transloco.translate("mailmessages.periodicDetail.notificationDeleted"));
                this.cerrixTab.close(false);
            });
    }

    validate(): boolean {
        const errors: string[] = [];
        if (isEmpty(this.notification.name)) {
            errors.push(this.transloco.translate("mailmessages.periodicDetail.nameRequiredError"));
        }

        if (!this.dayNumbers.find((x) => x.ID === this.notification.dayNumber)) {
            errors.push(this.transloco.translate("mailmessages.periodicDetail.invalidDaySelectedError"));
        }

        if (isEmpty(this.notification.subject)) {
            errors.push(this.transloco.translate("mailmessages.periodicDetail.subjectRequiredError"));
        }

        const messageEmpty = this.editorComponent.isEmpty();

        if (messageEmpty) {
            this.isEmpty = true;
            errors.push(this.transloco.translate("mailmessages.periodicDetail.messageRequiredError"));
        }

        if (!this.incidentForm.valid) {
            FormValidationHelper.markAllAsTouched(this.incidentForm);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.incidentForm);
            let validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);
            errors.push(validationMessage);
        }

        if (errors.any()) {
            this.toastr.warning(errors.join("<br />"), this.transloco.translate("mailmessages.periodicDetail.validationsTitle"), {
                enableHtml: true,
            });
        }

        return errors.length === 0;
    }

    setFocusToSubject() {
        this.subjectHasFocus = true;
    }

    deFocusSubject() {
        this.subjectHasFocus = false;
    }
}
